import { Alert, Button, Chip, Divider, Icons, Stack, Typography } from '@healthinal/ui';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { isAxiosError } from 'axios';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  createImportIdempotencyKey,
  getImportFileErrorMessage,
  getImportSubmitErrorResponse,
  ImportPreviewRowDto,
  ImportRowErrorDto,
  ImportSubmitErrorCode,
  ImportSubmitRowDto,
  useGetImportPreview,
  useSubmitImport,
} from '../../../api/created/importFile.ts';
import { getGetScreeningCaseByIdQueryKey, getGetScreeningCasesQueryKey } from '../../../api/created/screening.ts';
import { getGetTracingContactsQueryKey } from '../../../api/generated/contact-tracing-controller/contact-tracing-controller.ts';
import { getGetTracingContactSummaryQueryKey } from '../../../api/generated/summary-controller/summary-controller.ts';
import { ContentSection } from '../../../components/ContentSection.tsx';
import {
  getImportRowErrorMessage,
  getImportSubmitMessage,
  toDisplayRowNumber,
  UPLOAD_ALREADY_IMPORTED_MESSAGE,
  UPLOAD_EXPIRED_MESSAGE,
} from '../../../utils/importSubmitCode.ts';
import { ImportReviewTable } from '../components/ImportReviewTable.tsx';
import {
  getImportTarget,
  getImportTargetIds,
  IMPORT_TARGET_TEXTS,
  ImportTarget,
  ImportTargetSearchParams,
} from '../components/importTarget.ts';
import { getSuggestedDecision, isLinkAction, RowDecisions } from '../components/rowDecision.ts';

// The review of an uploaded person list, for a contact-tracing case (?patientId=) or a screening case
// (?screeningCaseId=).
export const Route = createFileRoute('/review/$fileId/')({
  component: ImportReview,
  validateSearch: (search): ImportTargetSearchParams => ({
    patientId: typeof search?.patientId === 'string' ? search.patientId : undefined,
    screeningCaseId: typeof search?.screeningCaseId === 'string' ? search.screeningCaseId : undefined,
  }),
});

function ImportReview() {
  const { fileId } = Route.useParams();
  const target = getImportTarget(Route.useSearch());
  const targetIds = target ? getImportTargetIds(target) : { patientId: null, screeningCaseId: null };
  const texts = IMPORT_TARGET_TEXTS[target?.type ?? 'CONTACT_TRACING'];
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: preview, isLoading, error } = useGetImportPreview({ fileId, ...targetIds });

  // Every row starts with the preview's suggestion, the user can change it per row.
  const [decisions, setDecisions] = useState<RowDecisions>({});
  useEffect(() => {
    if (preview) {
      setDecisions(Object.fromEntries(preview.rows.map((row) => [row.rowNumber, getSuggestedDecision(row)])));
    }
  }, [preview]);

  const submitRows = preview ? getSubmitRows(preview.rows, decisions) : [];

  // One key per review, so a double click doesn't import the rows twice.
  const idempotencyKey = useRef(createImportIdempotencyKey());
  const [submitErrorMessage, setSubmitErrorMessage] = useState<string | null>(null);
  // Problems with the upload itself, which no change on this page can fix.
  const [submitUploadProblem, setSubmitUploadProblem] = useState<UploadProblem | null>(null);
  const previewUploadProblem = getPreviewUploadProblem(error);
  // IMPORT.ROWS_INVALID names every problem by row. A row's problems are dropped once its decision changes.
  const [rowErrors, setRowErrors] = useState<ImportRowErrorDto[]>([]);
  const submitImportMutation = useSubmitImport();

  // Back to the page the import was started from.
  const handleCancel = () => {
    void navigate({ to: target?.type === 'SCREENING' ? '/screening' : '/contact-tracing' });
  };

  // Refreshes the lists the imported persons were added to and opens them.
  const showImportedPersons = (importTarget: ImportTarget) => {
    if (importTarget.type === 'CONTACT_TRACING') {
      void queryClient.invalidateQueries({ queryKey: getGetTracingContactsQueryKey() });
      void queryClient.invalidateQueries({ queryKey: getGetTracingContactSummaryQueryKey() });
      void navigate({ to: '/contact-tracing' });
    } else {
      void queryClient.invalidateQueries({ queryKey: getGetScreeningCaseByIdQueryKey(importTarget.screeningCaseId) });
      void queryClient.invalidateQueries({ queryKey: getGetScreeningCasesQueryKey() });
      void navigate({ to: '/screening/$screeningId', params: { screeningId: importTarget.screeningCaseId } });
    }
  };

  const handleSubmit = (importTarget: ImportTarget) => {
    setSubmitErrorMessage(null);
    setSubmitUploadProblem(null);
    setRowErrors([]);
    submitImportMutation.mutate(
      {
        data: { fileId, ...getImportTargetIds(importTarget), rows: submitRows },
        idempotencyKey: idempotencyKey.current,
      },
      {
        onSuccess: ({ created, linked }) => {
          toast.success(`${created + linked} ${texts.imported} (${created} neu erstellt, ${linked} verknüpft)`);
          showImportedPersons(importTarget);
        },
        onError: (submitError) => {
          // The rows may be changed before the next try, which then is a new request.
          idempotencyKey.current = createImportIdempotencyKey();
          const { code, rowErrors: rejectedRows } = getImportSubmitErrorResponse(submitError);
          setSubmitErrorMessage(getImportSubmitMessage(code));
          setSubmitUploadProblem(getSubmitUploadProblem(code));
          setRowErrors(rejectedRows ?? []);
        },
      },
    );
  };

  return (
    <ContentSection>
      <Stack gap={2}>
        <Typography level={'h3'}>{texts.title}</Typography>
        {preview && (
          <Stack direction={'row'} gap={1} flexWrap={'wrap'}>
            <Chip variant={'soft'}>{preview.rowCount} Zeilen</Chip>
            <Chip variant={'soft'} color={'danger'}>
              {preview.fullMatchCount} bereits vorhanden
            </Chip>
            <Chip variant={'soft'} color={'warning'}>
              {preview.partialMatchCount} teilweise Übereinstimmung
            </Chip>
          </Stack>
        )}
        <Divider />

        {!target && (
          <Alert color={'danger'} startDecorator={<Icons.ErrorOutline />}>
            Es wurde kein Indexpatient oder Screening-Auftrag angegeben.
          </Alert>
        )}
        {isLoading && <Typography>Datei wird geladen...</Typography>}
        {error && (
          <Alert
            color={'danger'}
            startDecorator={<Icons.ErrorOutline />}
            endDecorator={
              previewUploadProblem && (
                <UploadProblemButton
                  problem={previewUploadProblem}
                  onUploadAgain={handleCancel}
                  onShowImported={() => target && showImportedPersons(target)}
                />
              )
            }>
            {previewUploadProblem === 'ALREADY_IMPORTED'
              ? UPLOAD_ALREADY_IMPORTED_MESSAGE
              : previewUploadProblem === 'UNAVAILABLE'
                ? UPLOAD_EXPIRED_MESSAGE
                : getImportFileErrorMessage(error, texts.loadError)}
          </Alert>
        )}
        {target &&
          preview &&
          (preview.rows.length > 0 ? (
            <ImportReviewTable
              targetType={target.type}
              rows={preview.rows}
              decisions={decisions}
              onDecisionChange={(rowNumber, decision) => {
                setDecisions((current) => ({ ...current, [rowNumber]: decision }));
                setRowErrors((current) => current.filter((rowError) => rowError.rowNumber !== rowNumber));
              }}
            />
          ) : (
            <Alert startDecorator={<Icons.InfoOutlined />}>{texts.noPersons}</Alert>
          ))}

        {submitErrorMessage && (
          <Alert
            color={'danger'}
            startDecorator={<Icons.ErrorOutline />}
            endDecorator={
              submitUploadProblem && (
                <UploadProblemButton
                  problem={submitUploadProblem}
                  onUploadAgain={handleCancel}
                  onShowImported={() => target && showImportedPersons(target)}
                />
              )
            }
            sx={{ alignItems: 'flex-start' }}>
            <Stack gap={0.5}>
              <Typography color={'danger'} fontWeight={'lg'}>
                {submitErrorMessage}
              </Typography>
              {rowErrors.map((rowError, index) => (
                <Typography key={index} level={'body-sm'} color={'danger'}>
                  Zeile {toDisplayRowNumber(rowError.rowNumber)}:{' '}
                  {getImportRowErrorMessage(rowError, texts.alreadyAssigned)}
                </Typography>
              ))}
            </Stack>
          </Alert>
        )}

        <Divider />
        <Stack direction="row" justifyContent="flex-end" alignItems="center" gap={1}>
          {preview && (
            <Typography level={'body-sm'} textColor={'text.tertiary'} sx={{ mr: 'auto' }}>
              {submitRows.length} von {preview.rowCount} Zeilen werden importiert
            </Typography>
          )}
          <Button endDecorator={<Icons.Close />} color="neutral" onClick={handleCancel}>
            Abbrechen
          </Button>
          <Button
            endDecorator={<Icons.Check />}
            disabled={!target || submitRows.length === 0}
            loading={submitImportMutation.isPending}
            onClick={() => target && handleSubmit(target)}>
            Speichern
          </Button>
        </Stack>
      </Stack>
    </ContentSection>
  );
}

// UNAVAILABLE: the upload is gone or older than a day, only a new upload helps.
// ALREADY_IMPORTED: the upload has been imported once; uploading it again would create duplicates.
type UploadProblem = 'UNAVAILABLE' | 'ALREADY_IMPORTED';

// Preview answers these with a status and an English text.
function getPreviewUploadProblem(error: unknown): UploadProblem | null {
  if (!isAxiosError(error)) {
    return null;
  }
  switch (error.response?.status) {
    case 409:
      return 'ALREADY_IMPORTED';
    case 410:
      return 'UNAVAILABLE';
    default:
      return null;
  }
}

// The Idempotency-Key filter's own 409 has no code, so it isn't taken for an imported upload.
function getSubmitUploadProblem(code: ImportSubmitErrorCode | undefined): UploadProblem | null {
  switch (code) {
    case 'FILE.ALREADY_SUBMITTED':
      return 'ALREADY_IMPORTED';
    case 'FILE.EXPIRED':
    case 'FILE.NOT_FOUND':
      return 'UNAVAILABLE';
    default:
      return null;
  }
}

// Back to the upload, or to the persons the file has added already.
function UploadProblemButton({
  problem,
  onUploadAgain,
  onShowImported,
}: {
  problem: UploadProblem;
  onUploadAgain: () => void;
  onShowImported: () => void;
}) {
  return problem === 'ALREADY_IMPORTED' ? (
    <Button
      size={'sm'}
      variant={'solid'}
      color={'danger'}
      endDecorator={<Icons.ArrowForward />}
      onClick={onShowImported}>
      Zur Übersicht
    </Button>
  ) : (
    <Button size={'sm'} variant={'solid'} color={'danger'} endDecorator={<Icons.UploadFile />} onClick={onUploadAgain}>
      Erneut hochladen
    </Button>
  );
}

// Skipped rows and rows with errors are left out, the values go back as the preview sent them.
function getSubmitRows(rows: ImportPreviewRowDto[], decisions: RowDecisions): ImportSubmitRowDto[] {
  return rows.flatMap((row) => {
    const decision = decisions[row.rowNumber];
    if (!decision || decision.action === 'SKIP' || row.errors.length > 0) {
      return [];
    }
    return [
      {
        rowNumber: row.rowNumber,
        action: decision.action,
        personId: isLinkAction(decision.action) ? decision.personId : null,
        values: row.values,
      },
    ];
  });
}
